import { QC_REWORK_PREFIX, type StageHistoryEntry } from './kit';
import type { Stage } from './stage';

/**
 * The work on a case as Final Check reads it. The request of 7 October 2026,
 * section 4: who worked the case, in order, at which department, from when to
 * when and for how long, with every flag and every rework marked, and how long
 * the lab has had the case in all.
 *
 * It is the case's bench entries read a second way, never a second record: the
 * history card lists them newest first among every other action on the case,
 * and this lists them oldest first with nothing else, one line a step.
 */

/**
 * Who may read the bench entries of a tray: a holder of the case log, and
 * whoever passes or fails cases at Final Check, who reads the work before
 * deciding on it. The log itself, every action on the case, stays behind
 * `seeCaseLog` alone. The API's `CaseLogRules.SeesWorkSteps`.
 */
export function seesWorkSteps(seesCaseLog: boolean, decidesFinalCheck: boolean): boolean {
  return seesCaseLog || decidesFinalCheck;
}

/** The two ways a step ends by sending the case somewhere with a reason. */
export type WorkMark = 'flag' | 'rework';

export interface WorkStep {
  id: string;
  /** The tray the step was worked on, where the server says. */
  kitId: string | null;
  stage: Stage;
  byUserName: string | null;
  startedAt: string;
  /** Null while the step is still open. */
  endedAt: string | null;
  /** Seconds on the clock: the time worked. */
  activeSeconds: number;
  /** Seconds from the start to the finish, or to now for a step still open. Never negative. */
  elapsedSeconds: number;
  /**
   * How the step ended, when it ended by sending the case elsewhere: flagged
   * by the bench, or failed at Final Check and sent for rework.
   */
  sent: WorkMark | null;
  /** The reason that was given, without the words a rework is stored with in front. */
  reason: string | null;
  /**
   * What the system wrote on the entry as it closed it, when it was not a
   * person's reason: the case was taken over, moved, archived or deleted.
   */
  systemNote: string | null;
  /** Why the step happened at all, when the step before it on the tray sent the case here. */
  after: WorkMark | null;
}

export interface WorkSummary {
  /** Oldest first: the order the work was done in. */
  steps: WorkStep[];
  /** The time worked on the case, every step together. */
  workedSeconds: number;
  flags: number;
  reworks: number;
}

/** Whole seconds between two instants, and never less than none. */
function secondsBetween(fromMs: number, toMs: number): number {
  return Math.max(0, Math.round((toMs - fromMs) / 1000));
}

/**
 * The notes the system writes itself on an entry it closes: a take-over, a
 * move, an archive, a deletion. `takeOverNote` and the closed notes of the
 * archive and the deletion. Nobody flagged anything by them.
 */
const SYSTEM_NOTE = /^(Taken by |Sent to |Archived$|Deleted$)/;

function isSystemNote(note: string): boolean {
  return SYSTEM_NOTE.test(note);
}

function markOf(note: string | null): WorkMark | null {
  if (note === null || note.trim() === '' || isSystemNote(note)) return null;
  return note.startsWith(QC_REWORK_PREFIX) ? 'rework' : 'flag';
}

function reasonOf(note: string): string {
  return note.startsWith(QC_REWORK_PREFIX) ? note.slice(QC_REWORK_PREFIX.length) : note;
}

/**
 * The bench entries of a case as its steps, oldest first.
 *
 * A step is marked by how it ended, off the note its entry was closed with,
 * which is the one place a flag's reason and a rework's reason are both kept.
 * The step that follows on the same tray is the one the case was sent to, so
 * it carries the same mark as its cause. An entry still open has sent the case
 * nowhere, whatever note it carries.
 */
export function workSummary(history: readonly StageHistoryEntry[], nowMs: number): WorkSummary {
  const ordered = [...history].sort(
    (a, b) => a.startedAt.localeCompare(b.startedAt) || a.id.localeCompare(b.id),
  );

  // The mark the last finished step of each tray left behind, for the step after it.
  const left = new Map<string, WorkMark | null>();
  const steps = ordered.map((entry): WorkStep => {
    const tray = entry.kitId ?? '';
    const sent = entry.endedAt === null ? null : markOf(entry.note);
    const step: WorkStep = {
      id: entry.id,
      kitId: entry.kitId ?? null,
      stage: entry.stage,
      byUserName: entry.byUserName,
      startedAt: entry.startedAt,
      endedAt: entry.endedAt,
      activeSeconds: Math.max(0, entry.activeSeconds),
      elapsedSeconds: secondsBetween(
        Date.parse(entry.startedAt),
        entry.endedAt === null ? nowMs : Date.parse(entry.endedAt),
      ),
      sent,
      reason: sent === null ? null : reasonOf(entry.note as string),
      systemNote:
        entry.endedAt !== null && entry.note !== null && isSystemNote(entry.note) ? entry.note : null,
      after: left.get(tray) ?? null,
    };
    left.set(tray, sent);
    return step;
  });

  return {
    steps,
    workedSeconds: steps.reduce((sum, step) => sum + step.activeSeconds, 0),
    flags: steps.filter((step) => step.sent === 'flag').length,
    reworks: steps.filter((step) => step.sent === 'rework').length,
  };
}

/**
 * How long the lab has had the case: from the moment it was booked in to now,
 * in seconds. Null for a case with no such moment, one from an API that does
 * not send it among them.
 */
export function secondsSinceReceived(
  receivedAt: string | null | undefined,
  nowMs: number,
): number | null {
  if (receivedAt === null || receivedAt === undefined) return null;
  const from = Date.parse(receivedAt);
  return Number.isNaN(from) ? null : secondsBetween(from, nowMs);
}

/**
 * A length of time that may run to days, for a figure that is read rather than
 * watched: `45m`, `3h 20m`, `2d 4h`. Minutes are dropped once there are days;
 * nobody plans by the minute across a week.
 */
export function formatSpan(totalSeconds: number): string {
  const minutes = Math.max(0, Math.round(totalSeconds / 60));
  if (minutes < 60) return `${String(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest === 0 ? `${String(hours)}h` : `${String(hours)}h ${String(rest)}m`;
  }
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours === 0 ? `${String(days)}d` : `${String(days)}d ${String(restHours)}h`;
}
